import type {
  CardDef,
  Command,
  GameCredentials,
  GameView,
  HistoryEntry,
  RewindProposal,
  Scenario,
  Session,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, session?: Session): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("content-type", "application/json");
  if (session) headers.set("authorization", `Bearer ${session.token}`);
  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError("Geen verbinding met de spelserver", 0, "offline");
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = body?.error;
    throw new ApiError(error?.message || "Het verzoek is mislukt", response.status, error?.code || "unknown", error?.details);
  }
  return body as T;
}

export const api = {
  create(name: string, scenario: Scenario): Promise<GameCredentials> {
    return request("/api/games", { method: "POST", body: JSON.stringify({ name, scenario }) });
  },
  join(gameId: string, name: string, joinCode: string): Promise<GameCredentials> {
    return request(`/api/games/${encodeURIComponent(gameId)}/join`, {
      method: "POST",
      body: JSON.stringify({ name, join_code: joinCode }),
    });
  },
  state(session: Session): Promise<GameView> {
    return request(`/api/games/${session.gameId}/state`, {}, session);
  },
  action(session: Session, expectedVersion: number, idempotencyKey: string, command: Command): Promise<GameView> {
    return request(
      `/api/games/${session.gameId}/actions`,
      {
        method: "POST",
        body: JSON.stringify({
          expected_version: expectedVersion,
          idempotency_key: idempotencyKey,
          command,
        }),
      },
      session,
    );
  },
  cards(): Promise<CardDef[]> {
    return request<{ cards: CardDef[] }>("/api/cards").then((result) => result.cards);
  },
  history(session: Session): Promise<HistoryEntry[]> {
    return request<{ entries: HistoryEntry[] }>(`/api/games/${session.gameId}/history?limit=160`, {}, session).then(
      (result) => result.entries,
    );
  },
  recovery(session: Session): Promise<RewindProposal | null> {
    return request<{ proposal: RewindProposal | null }>(`/api/games/${session.gameId}/recovery`, {}, session).then(
      (result) => result.proposal,
    );
  },
  proposeRecovery(session: Session, targetVersion: number): Promise<RewindProposal> {
    return request(
      `/api/games/${session.gameId}/recovery/propose`,
      { method: "POST", body: JSON.stringify({ target_version: targetVersion }) },
      session,
    );
  },
  resolveRecovery(session: Session, proposalId: string, accept: boolean): Promise<{ accepted: boolean; state?: GameView }> {
    return request(
      `/api/games/${session.gameId}/recovery/${proposalId}/resolve`,
      { method: "POST", body: JSON.stringify({ accept }) },
      session,
    );
  },
};

export function websocketUrl(session: Session): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${location.host}/api/games/${session.gameId}/ws`;
}
